import React from "react";
import {
  Avatar,
  Box,
  Card,
  Chip,
  Divider,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Person as PersonIcon,
  StarBorder as StarBorderIcon,
  Star as StarIcon,
} from "@mui/icons-material";
import { WorkItemList } from "../../../store/services/timelineApiRTK";
import { isOverdue } from "../../../utils/activityHelpers";
import { formatSmartDateForWorkItem } from "../../../utils/dateFormatters";
import { getTeamColor, getWorkItemColor, getWorkItemIcon, getWorkItemTypeName, WORK_ITEM_CATEGORIES } from "../../../utils/workItemUtils";
import ActivityLink from "../../activity-link/ActivityLink";
import { formatTimeAgo } from "../../customer-list/components/dateUtils";


interface ActivityCardViewProps {
  activities: WorkItemList[];
  favorites: number[];
  onToggleFavorite: (activityId: number) => void;
}

const ActivityCardView: React.FC<ActivityCardViewProps> = ({
  activities,
  favorites,
  onToggleFavorite,
}) => (
  <Grid container spacing={3}>
    {activities.map((activity) => {
      const scheduleInfo = formatSmartDateForWorkItem(activity.ScheduledTo);
      
      return (
        <Grid item xs={12} md={6} lg={4} key={activity.Id}>
          <ActivityLink
            style={{ textDecoration: "none" }}
            workItemNo={activity.Id}
            customerNo={activity.Customer?.Id}
            to={`/activity/${activity.Id}`}
          >
            <Card
              elevation={2}
              sx={{
                height: "100%",
                transition: "all 0.2s",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: 5,
                },
                ...(isOverdue(activity) && {
                  borderLeft: "4px solid #f44336",
                }),
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  display: "flex",
                  p: 2,
                  bgcolor: getTeamColor(activity.Team?.Id),
                  color: "white",
                  alignItems: "center",
                  minHeight: 64,
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: "rgba(255,255,255,0.2)",
                    color: "white",
                    width: 40,
                    height: 40,
                    fontSize: "1.1rem",
                    fontWeight: "bold",
                    mr: 2,
                  }}
                >
                  {activity.Customer?.Name?.charAt(0) || "?"}
                </Avatar>
                
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontSize: "1rem",
                      fontWeight: 600,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap"
                    }}
                  >
                    {activity.Customer?.Name || "Ingen kund"}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      opacity: 0.9,
                      fontSize: "0.8rem"
                    }}
                  >
                    {activity.Customer?.CustomerId || "Inget kundnummer"}
                  </Typography>
                </Box>
                
                <Tooltip
                  title={
                    favorites.includes(activity.Id)
                      ? "Ta bort från favoriter"
                      : "Lägg till som favorit"
                  }
                >
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      onToggleFavorite(activity.Id);
                    }}
                    sx={{ color: "white" }}
                  >
                    {favorites.includes(activity.Id) ? (
                      <StarIcon />
                    ) : (
                      <StarBorderIcon />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Content */}
              <Box sx={{ p: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: `${getWorkItemColor(activity.Type)}20`,
                      color: getWorkItemColor(activity.Type),
                      width: 32,
                      height: 32,
                      borderRadius: 1,
                      mr: 1.5,
                      border: `2px solid ${getWorkItemColor(activity.Type)}40`,
                    }}
                  >
                    {getWorkItemIcon(activity.Type)}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap"
                      }}
                    >
                      {activity.Name}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ fontSize: "0.8rem" }}
                      >
                        {getWorkItemTypeName(activity.Type)}
                      </Typography>
                      <Chip
                        size="small"
                        label={
                          Object.entries(WORK_ITEM_CATEGORIES).find(([, types]) =>
                            types.includes(activity.Type)
                          )?.[0] || "Övrigt"
                        }
                        sx={{
                          height: 18,
                          fontSize: "0.65rem",
                          bgcolor: `${getWorkItemColor(activity.Type)}15`,
                          color: getWorkItemColor(activity.Type),
                          border: `1px solid ${getWorkItemColor(activity.Type)}40`,
                        }}
                      />
                    </Box>
                  </Box>
                </Box>

                {/* Schedule info */}
                <Box 
                  sx={{ 
                    p: 2, 
                    borderRadius: 2, 
                    bgcolor: scheduleInfo.color === "error" ? "error.light" : 
                              scheduleInfo.color === "success" ? "success.light" :
                              scheduleInfo.color === "warning" ? "warning.light" :
                              scheduleInfo.color === "info" ? "info.light" : "grey.100",
                    color: scheduleInfo.color === "error" ? "error.dark" : 
                           scheduleInfo.color === "success" ? "success.dark" :
                           scheduleInfo.color === "warning" ? "warning.dark" :
                           scheduleInfo.color === "info" ? "info.dark" : "text.primary",
                    mb: 2,
                    display: "flex",
                    alignItems: "center"
                  }}
                >
                  {scheduleInfo.icon}
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      ml: 1, 
                      fontWeight: 600,
                      fontSize: "0.9rem"
                    }}
                  >
                    {scheduleInfo.display}
                  </Typography>
                </Box>

                {activity.Description && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ 
                      mb: 2,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      fontSize: "0.85rem",
                      whiteSpace: "pre-wrap"
                    }}
                  >
                    {activity.Description}
                  </Typography>
                )}

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                  <Chip
                    icon={<BusinessIcon fontSize="small" />}
                    label={activity.Team?.Name || "Ej tilldelat"}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: getTeamColor(activity.Team?.Id),
                      color: getTeamColor(activity.Team?.Id),
                      fontSize: "0.75rem",
                      height: 24
                    }}
                  />

                  <Chip
                    label={
                      Object.entries(WORK_ITEM_CATEGORIES).find(([, types]) =>
                        types.includes(activity.Type)
                      )?.[0] || "Övrigt"
                    }
                    size="small"
                    sx={{
                      bgcolor: `${getWorkItemColor(activity.Type)}`,
                      color: "white",
                      fontSize: "0.75rem",
                      height: 24,
                      fontWeight: 600,
                      "& .MuiChip-label": {
                        textTransform: "uppercase",
                        letterSpacing: "0.5px"
                      }
                    }}
                  />

                  {activity.State === 2 && (
                    <Chip
                      icon={<CheckCircleIcon fontSize="small" />}
                      label="Slutförd"
                      size="small"
                      color="success"
                      sx={{ fontSize: "0.75rem", height: 24 }}
                    />
                  )}
                </Box>

                <Divider sx={{ my: 1 }} />

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mt: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <PersonIcon fontSize="small" color="action" />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ ml: 0.5, fontSize: "0.8rem" }}
                    >
                      {activity.AssignedTo?.Name || "Ej tilldelad"}
                    </Typography>
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: "0.75rem" }}
                  >
                    Uppdaterad {formatTimeAgo(activity.Modified)}
                  </Typography>
                </Box>
              </Box>
            </Card>
          </ActivityLink>
        </Grid>
      );
    })}
  </Grid>
);

export default ActivityCardView;